import { useState } from 'react';
import type { App, Environment, Platform } from '@hangar/shared';
import { useEnvVarMutations } from '@/api/hooks';
import type { EnvResolution } from '@/api/schemas';
import { initialResolveSelection, resolveBody } from './resolveModel';

export interface ResolveInitial {
  apps: App[] | undefined;
  environments: Environment[];
  initialAppSlug?: string;
  initialEnvironment?: string;
  initialPlatform?: Platform;
}

/** The selections that feed the resolve request. */
function useResolveInputs(initial: ResolveInitial) {
  const first = initialResolveSelection(initial);
  const [appSlug, setAppSlug] = useState(first.appSlug);
  const [environment, setEnvironment] = useState(first.environment);
  const [platform, setPlatform] = useState<Platform>(first.platform);
  const [ciVariables, setCiVariables] = useState('');
  const app = initial.apps?.find((a) => a.slug === appSlug);
  return { appSlug, setAppSlug, environment, setEnvironment, platform, setPlatform, ciVariables, setCiVariables, app, ready: Boolean(app && environment) };
}

/** Inputs, the `POST /apps/{id}/env/resolve` call and its result. */
export function useResolvePreview(initial: ResolveInitial) {
  const { resolve } = useEnvVarMutations();
  const inputs = useResolveInputs(initial);
  const [result, setResult] = useState<EnvResolution | null>(null);
  const { app, environment, platform, ciVariables } = inputs;

  const run = async () => {
    if (!app) return;
    try {
      setResult(await resolve.mutateAsync({ appId: app.id, body: resolveBody(environment, platform, ciVariables) }));
    } catch {
      /* ProblemAlert */
    }
  };
  return { ...inputs, result, run, isPending: resolve.isPending, error: resolve.error };
}

export type ResolvePreviewApi = ReturnType<typeof useResolvePreview>;
